import { useEffect, useState } from "react";
import { Download, Search } from "lucide-react";
import { AuditEntry, auditApi, downloadAuthed, timeAgo } from "@/lib/catalog-api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/catalog/Skeleton";

export default function AuditLog() {
  const [rows, setRows] = useState<AuditEntry[] | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");
  const [expanded, setExpanded] = useState<string | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 350);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    let alive = true;
    setRows(null);
    auditApi.list({ q: debounced || undefined, page, page_size: 50 })
      .then((r) => { if (!alive) return; setRows(r.rows); setTotal(r.total); })
      .catch(() => alive && setRows([]));
    return () => { alive = false; };
  }, [debounced, page]);

  async function exportCsv() {
    const sp = new URLSearchParams();
    if (debounced) sp.set("q", debounced);
    const stamp = new Date().toISOString().slice(0, 10);
    await downloadAuthed(`/audit-log/export${sp.toString() ? `?${sp}` : ""}`, `audit-log-${stamp}.csv`);
  }

  return (
    <main className="flex-1 px-6 md:px-10 py-10" data-testid="audit-log-page">
      <p className="text-[11px] uppercase tracking-[0.28em] text-oxblood font-medium mb-2">— Admin</p>
      <h1 className="font-display text-3xl sm:text-4xl">Audit log</h1>
      <p className="mt-2 text-sm text-muted-foreground">Every meaningful change. Filterable, exportable.</p>

      <div className="mt-8 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            data-testid="audit-search"
            placeholder="Search action, actor, resource…"
            value={q} onChange={(e: any) => setQ(e.target.value)}
            className="pl-9 h-10"
          />
        </div>
        <Button variant="outline" onClick={exportCsv}
          data-testid="audit-export-btn"
          className="rounded-none border-foreground/20 hover:bg-bone h-10">
          <Download className="w-4 h-4 mr-2" /> Export CSV
        </Button>
        <span className="text-xs text-muted-foreground" data-testid="audit-total">
          {rows === null ? "Loading…" : `${total} event${total === 1 ? "" : "s"}`}
        </span>
      </div>

      <div className="mt-6">
        {rows === null ? (
          <div className="space-y-1.5">
            {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-12" />)}
          </div>
        ) : rows.length === 0 ? (
          <div className="border border-border bg-bone/30 p-12 text-center" data-testid="audit-empty">
            <p className="font-display text-2xl">Quiet so far.</p>
            <p className="text-sm text-muted-foreground mt-2">No events match these filters.</p>
          </div>
        ) : (
          <ul className="divide-y divide-border border-y border-border" data-testid="audit-list">
            {rows.map((r) => (
              <li key={r.id} className="text-sm" data-testid={`audit-row-${r.id}`}>
                <button type="button"
                  onClick={() => setExpanded(expanded === r.id ? null : r.id)}
                  className="w-full text-left grid grid-cols-[1fr_auto] gap-3 px-3 py-3 hover:bg-bone/40">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium truncate">{r.action_label}</span>
                      <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                        {r.action}
                      </span>
                    </div>
                    <p className="text-[12px] text-muted-foreground truncate mt-0.5">
                      {r.actor_email || "system"}
                      {r.resource_label ? ` · ${r.resource_label}` : ""}
                      {r.ip_address ? ` · ${r.ip_address}` : ""}
                    </p>
                  </div>
                  <span className="text-[11px] text-muted-foreground tabular-nums shrink-0"
                    title={r.created_at}>
                    {timeAgo(r.created_at)}
                  </span>
                </button>
                {expanded === r.id && (
                  <pre className="px-3 pb-3 text-[11px] text-muted-foreground whitespace-pre-wrap font-mono">
{JSON.stringify(r.metadata || {}, null, 2)}
                  </pre>
                )}
              </li>
            ))}
          </ul>
        )}

        {rows && total > 50 && (
          <div className="mt-6 flex justify-center gap-2 text-sm">
            <button type="button" onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-3 py-1.5 border border-border disabled:opacity-30 hover:text-oxblood">Prev</button>
            <span className="self-center text-muted-foreground">Page {page}</span>
            <button type="button" onClick={() => setPage((p) => p + 1)}
              disabled={page * 50 >= total}
              className="px-3 py-1.5 border border-border disabled:opacity-30 hover:text-oxblood">Next</button>
          </div>
        )}
      </div>
    </main>
  );
}
